import type { LangMap } from "./index";

const en = {
  title: "Programs",
  subtitle: "Your school year, turned into a study path",
  yourYear: "Your school year",
  completed: "of the program completed",
  empty: "No program yet. Import your syllabus to get started.",
  import: "Import your program",
  importDesc:
    "Upload a PDF, a photo of your syllabus, paste the text or type it in. The AI turns it into units and topics — you can fix anything before saving.",
  classeviva:
    "Official ClasseViva sync is not available yet: as soon as an authorised API exists it will fill this section automatically, with no changes for you.",
  paste: "Paste or type your program here…",
  analyze: "Analyse with AI",
  analyzing: "Reading your program…",
  review: "Check and correct",
  confirm: "Confirm import",
  subject: "Subject",
  teacher: "Teacher",
  schoolYear: "School year",
  unit: "Unit",
  topics: "topics",
  addTopic: "Add topic",
  addUnit: "Add unit",
  remove: "Remove",
  saved: "Program imported",
  annualProgram: "Annual program",
  legend: "✓ done · ◐ in progress · ○ not started · ⭐ exam",
  status: {
    not_started: "Not started",
    in_progress: "In progress",
    done: "Done",
  },
  topicPage: {
    progress: "Progress",
    estimated: "Estimated time",
    difficulty: "Difficulty",
    knowledge: "Readiness",
    study: "Study",
    theory: "Theory",
    exercises: "Exercises",
    tools: "Tools",
    materials: "My materials",
    appTheory: "App theory",
    myTheory: "My theory",
    generate: "Generate theory",
    simplify: "Simplify",
    deepen: "Go deeper",
    different: "Explain differently",
    example: "Give me an example",
    read: "Read it to me",
    addMaterial: "Add material",
    materialPlaceholder: "Paste notes, or attach a photo / audio…",
    materialTitle: "Material name",
    save: "Save",
    noMaterials: "No material yet.",
  },
  knowledge: {
    to_start: "To start",
    learning: "Learning",
    almost: "Almost ready",
    ready: "Ready",
    review: "Needs review",
  },
  wizard: {
    cta: "STUDY THIS TOPIC",
    when: "When is your exam?",
    howLong: "How long can you study?",
    from: "What do you want to study from?",
    sourceApp: "App theory",
    sourceMine: "My materials",
    sourceBoth: "Both",
    how: "How do you want to prepare?",
    styleAi: "AI recommended plan",
    styleFast: "Fast",
    styleDeep: "In depth",
    styleReview: "Review",
    styleCustom: "Custom",
    notes: "Anything else the AI should know?",
    create: "Create my plan",
    creating: "Building your plan…",
    custom: "Custom",
  },
  plan: {
    title: "Your plan",
    daysLeft: "{{count}} days left",
    tomorrow: "EXAM TOMORROW",
    today: "TODAY",
    needed: "Estimated time needed",
    start: "Start",
    done: "Done",
    regenerate: "Reorganise plan",
    missed: "You didn't finish yesterday's session. Want to reorganise the plan?",
    inPlanner: "Sessions added to your Planner",
  },
  quick: {
    title: "I don't have much time",
    minutes: "{{n}} minutes",
    go: "What should I do now?",
  },
  toolsFor: "Suggested tools for this subject",
};

const it: typeof en = {
  title: "Programmi",
  subtitle: "Il tuo anno scolastico, trasformato in un percorso di studio",
  yourYear: "Il tuo anno scolastico",
  completed: "del programma completato",
  empty: "Nessun programma. Importa il tuo programma per iniziare.",
  import: "Importa il tuo programma",
  importDesc:
    "Carica un PDF, una foto del programma, incolla il testo o scrivilo a mano. L'AI lo trasforma in unità e argomenti — puoi correggere tutto prima di salvare.",
  classeviva:
    "L'integrazione ufficiale con ClasseViva non è ancora disponibile: appena esisterà un'API autorizzata compilerà questa sezione automaticamente, senza cambiare nulla per te.",
  paste: "Incolla o scrivi qui il tuo programma…",
  analyze: "Analizza con l'AI",
  analyzing: "Sto leggendo il programma…",
  review: "Controlla e correggi",
  confirm: "Conferma importazione",
  subject: "Materia",
  teacher: "Docente",
  schoolYear: "Anno scolastico",
  unit: "Unità",
  topics: "argomenti",
  addTopic: "Aggiungi argomento",
  addUnit: "Aggiungi unità",
  remove: "Rimuovi",
  saved: "Programma importato",
  annualProgram: "Programma annuale",
  legend: "✓ completato · ◐ in corso · ○ non iniziato · ⭐ verifica",
  status: {
    not_started: "Non iniziato",
    in_progress: "In corso",
    done: "Completato",
  },
  topicPage: {
    progress: "Progresso",
    estimated: "Tempo stimato",
    difficulty: "Difficoltà",
    knowledge: "Preparazione",
    study: "Studia",
    theory: "Teoria",
    exercises: "Esercizi",
    tools: "Strumenti",
    materials: "I miei materiali",
    appTheory: "Teoria app",
    myTheory: "La mia teoria",
    generate: "Genera teoria",
    simplify: "Semplifica",
    deepen: "Approfondisci",
    different: "Spiegamelo diversamente",
    example: "Fammi un esempio",
    read: "Leggimelo",
    addMaterial: "Aggiungi materiale",
    materialPlaceholder: "Incolla appunti, o allega una foto / audio…",
    materialTitle: "Nome del materiale",
    save: "Salva",
    noMaterials: "Nessun materiale.",
  },
  knowledge: {
    to_start: "Da iniziare",
    learning: "In apprendimento",
    almost: "Quasi pronto",
    ready: "Pronto",
    review: "Da ripassare",
  },
  wizard: {
    cta: "STUDIA QUESTO ARGOMENTO",
    when: "Quando hai la verifica?",
    howLong: "Quanto tempo puoi studiare?",
    from: "Da cosa vuoi studiare?",
    sourceApp: "Teoria dell'app",
    sourceMine: "I miei materiali",
    sourceBoth: "Entrambi",
    how: "Come vuoi prepararti?",
    styleAi: "Piano consigliato dall'AI",
    styleFast: "Veloce",
    styleDeep: "Approfondito",
    styleReview: "Ripasso",
    styleCustom: "Personalizzato",
    notes: "Altro che l'AI dovrebbe sapere?",
    create: "Crea il piano",
    creating: "Sto creando il piano…",
    custom: "Personalizzato",
  },
  plan: {
    title: "Il tuo piano",
    daysLeft: "Mancano {{count}} giorni",
    tomorrow: "VERIFICA DOMANI",
    today: "OGGI",
    needed: "Tempo necessario stimato",
    start: "Inizia",
    done: "Fatto",
    regenerate: "Riorganizza il piano",
    missed: "Non hai completato la sessione di ieri. Vuoi riorganizzare il piano?",
    inPlanner: "Sessioni aggiunte al Planner",
  },
  quick: {
    title: "Ho poco tempo",
    minutes: "{{n}} minuti",
    go: "Cosa mi conviene fare?",
  },
  toolsFor: "Strumenti consigliati per questa materia",
};


const es: typeof en = {
  title: "Programas",
  subtitle: "Tu año escolar, convertido en un plan de estudio",
  yourYear: "Tu año escolar",
  completed: "del programa completado",
  empty: "Aún no hay ningún programa. Importa tu temario para empezar.",
  import: "Importa tu programa",
  importDesc:
    "Sube un PDF, una foto de tu temario, pega el texto o escríbelo. La IA lo convierte en unidades y temas — puedes corregir todo antes de guardar.",
  classeviva:
    "La sincronización oficial con ClasseViva aún no está disponible: en cuanto exista una API autorizada, rellenará esta sección automáticamente, sin ningún cambio para ti.",
  paste: "Pega o escribe aquí tu programa…",
  analyze: "Analizar con IA",
  analyzing: "Leyendo tu programa…",
  review: "Revisa y corrige",
  confirm: "Confirmar importación",
  subject: "Asignatura",
  teacher: "Profesor",
  schoolYear: "Año escolar",
  unit: "Unidad",
  topics: "temas",
  addTopic: "Añadir tema",
  addUnit: "Añadir unidad",
  remove: "Eliminar",
  saved: "Programa importado",
  annualProgram: "Programa anual",
  legend: "✓ terminado · ◐ en curso · ○ sin empezar · ⭐ examen",
  status: {
    not_started: "Sin empezar",
    in_progress: "En curso",
    done: "Terminado",
  },
  topicPage: {
    progress: "Progreso",
    estimated: "Tiempo estimado",
    difficulty: "Dificultad",
    knowledge: "Preparación",
    study: "Estudiar",
    theory: "Teoría",
    exercises: "Ejercicios",
    tools: "Herramientas",
    materials: "Mis materiales",
    appTheory: "Teoría de la app",
    myTheory: "Mi teoría",
    generate: "Generar teoría",
    simplify: "Simplificar",
    deepen: "Profundizar",
    different: "Explícalo de otra forma",
    example: "Dame un ejemplo",
    read: "Léemelo",
    addMaterial: "Añadir material",
    materialPlaceholder: "Pega apuntes, o adjunta una foto / audio…",
    materialTitle: "Nombre del material",
    save: "Guardar",
    noMaterials: "Aún no hay material.",
  },
  knowledge: {
    to_start: "Por empezar",
    learning: "Aprendiendo",
    almost: "Casi listo",
    ready: "Listo",
    review: "A repasar",
  },
  wizard: {
    cta: "ESTUDIA ESTE TEMA",
    when: "¿Cuándo es tu examen?",
    howLong: "¿Cuánto tiempo puedes estudiar?",
    from: "¿A partir de qué quieres estudiar?",
    sourceApp: "Teoría de la app",
    sourceMine: "Mis materiales",
    sourceBoth: "Ambos",
    how: "¿Cómo quieres prepararte?",
    styleAi: "Plan recomendado por la IA",
    styleFast: "Rápido",
    styleDeep: "En profundidad",
    styleReview: "Repaso",
    styleCustom: "Personalizado",
    notes: "¿Algo más que la IA deba saber?",
    create: "Crear mi plan",
    creating: "Creando tu plan…",
    custom: "Personalizado",
  },
  plan: {
    title: "Tu plan",
    daysLeft: "Quedan {{count}} días",
    tomorrow: "EXAMEN MAÑANA",
    today: "HOY",
    needed: "Tiempo estimado necesario",
    start: "Empezar",
    done: "Hecho",
    regenerate: "Reorganizar plan",
    missed: "No terminaste la sesión de ayer. ¿Quieres reorganizar el plan?",
    inPlanner: "Sesiones añadidas a tu Planificador",
  },
  quick: {
    title: "No tengo mucho tiempo",
    minutes: "{{n}} minutos",
    go: "¿Qué debería hacer ahora?",
  },
  toolsFor: "Herramientas sugeridas para esta asignatura",
};

const fr: typeof en = {
  title: "Programmes",
  subtitle: "Ton année scolaire, transformée en parcours d'étude",
  yourYear: "Ton année scolaire",
  completed: "du programme terminé",
  empty: "Aucun programme pour l'instant. Importe ton programme pour commencer.",
  import: "Importe ton programme",
  importDesc:
    "Téléverse un PDF, une photo de ton programme, colle le texte ou saisis-le. L'IA le transforme en unités et sujets — tu peux tout corriger avant d'enregistrer.",
  classeviva:
    "La synchronisation officielle avec ClasseViva n'est pas encore disponible : dès qu'une API autorisée existera, elle remplira automatiquement cette section, sans rien changer pour toi.",
  paste: "Colle ou saisis ici ton programme…",
  analyze: "Analyser avec l'IA",
  analyzing: "Lecture de ton programme…",
  review: "Vérifie et corrige",
  confirm: "Confirmer l'importation",
  subject: "Matière",
  teacher: "Professeur",
  schoolYear: "Année scolaire",
  unit: "Unité",
  topics: "sujets",
  addTopic: "Ajouter un sujet",
  addUnit: "Ajouter une unité",
  remove: "Supprimer",
  saved: "Programme importé",
  annualProgram: "Programme annuel",
  legend: "✓ terminé · ◐ en cours · ○ non commencé · ⭐ examen",
  status: {
    not_started: "Non commencé",
    in_progress: "En cours",
    done: "Terminé",
  },
  topicPage: {
    progress: "Progression",
    estimated: "Temps estimé",
    difficulty: "Difficulté",
    knowledge: "Préparation",
    study: "Étudier",
    theory: "Théorie",
    exercises: "Exercices",
    tools: "Outils",
    materials: "Mes documents",
    appTheory: "Théorie de l'appli",
    myTheory: "Ma théorie",
    generate: "Générer la théorie",
    simplify: "Simplifier",
    deepen: "Approfondir",
    different: "Explique-le autrement",
    example: "Donne-moi un exemple",
    read: "Lis-le-moi",
    addMaterial: "Ajouter un document",
    materialPlaceholder: "Colle des notes, ou joins une photo / un audio…",
    materialTitle: "Nom du document",
    save: "Enregistrer",
    noMaterials: "Aucun document pour l'instant.",
  },
  knowledge: {
    to_start: "À commencer",
    learning: "En apprentissage",
    almost: "Presque prêt",
    ready: "Prêt",
    review: "À revoir",
  },
  wizard: {
    cta: "ÉTUDIE CE SUJET",
    when: "Quand as-tu ton examen ?",
    howLong: "Combien de temps peux-tu étudier ?",
    from: "À partir de quoi veux-tu étudier ?",
    sourceApp: "Théorie de l'appli",
    sourceMine: "Mes documents",
    sourceBoth: "Les deux",
    how: "Comment veux-tu te préparer ?",
    styleAi: "Plan recommandé par l'IA",
    styleFast: "Rapide",
    styleDeep: "Approfondi",
    styleReview: "Révision",
    styleCustom: "Personnalisé",
    notes: "Autre chose que l'IA devrait savoir ?",
    create: "Créer mon plan",
    creating: "Création de ton plan…",
    custom: "Personnalisé",
  },
  plan: {
    title: "Ton plan",
    daysLeft: "Il reste {{count}} jours",
    tomorrow: "EXAMEN DEMAIN",
    today: "AUJOURD'HUI",
    needed: "Temps estimé nécessaire",
    start: "Commencer",
    done: "Terminé",
    regenerate: "Réorganiser le plan",
    missed: "Tu n'as pas terminé la session d'hier. Veux-tu réorganiser le plan ?",
    inPlanner: "Sessions ajoutées à ton Planificateur",
  },
  quick: {
    title: "Je n'ai pas beaucoup de temps",
    minutes: "{{n}} minutes",
    go: "Que devrais-je faire maintenant ?",
  },
  toolsFor: "Outils suggérés pour cette matière",
};

const de: typeof en = {
  title: "Lehrpläne",
  subtitle: "Dein Schuljahr, verwandelt in einen Lernpfad",
  yourYear: "Dein Schuljahr",
  completed: "des Programms abgeschlossen",
  empty: "Noch kein Lehrplan. Importiere deinen Lehrplan, um zu starten.",
  import: "Lehrplan importieren",
  importDesc:
    "Lade ein PDF hoch, ein Foto deines Lehrplans, füge den Text ein oder tippe ihn ein. Die KI verwandelt ihn in Einheiten und Themen — du kannst vor dem Speichern alles korrigieren.",
  classeviva:
    "Die offizielle ClasseViva-Synchronisierung ist noch nicht verfügbar: Sobald eine autorisierte API existiert, wird dieser Bereich automatisch ausgefüllt, ohne dass sich für dich etwas ändert.",
  paste: "Füge deinen Lehrplan hier ein oder tippe ihn ein…",
  analyze: "Mit KI analysieren",
  analyzing: "Dein Programm wird gelesen…",
  review: "Überprüfen und korrigieren",
  confirm: "Import bestätigen",
  subject: "Fach",
  teacher: "Lehrkraft",
  schoolYear: "Schuljahr",
  unit: "Einheit",
  topics: "Themen",
  addTopic: "Thema hinzufügen",
  addUnit: "Einheit hinzufügen",
  remove: "Entfernen",
  saved: "Programm importiert",
  annualProgram: "Jahresprogramm",
  legend: "✓ erledigt · ◐ in Bearbeitung · ○ nicht begonnen · ⭐ Prüfung",
  status: {
    not_started: "Nicht begonnen",
    in_progress: "In Bearbeitung",
    done: "Erledigt",
  },
  topicPage: {
    progress: "Fortschritt",
    estimated: "Geschätzte Zeit",
    difficulty: "Schwierigkeit",
    knowledge: "Bereitschaft",
    study: "Lernen",
    theory: "Theorie",
    exercises: "Übungen",
    tools: "Werkzeuge",
    materials: "Meine Materialien",
    appTheory: "App-Theorie",
    myTheory: "Meine Theorie",
    generate: "Theorie erstellen",
    simplify: "Vereinfachen",
    deepen: "Vertiefen",
    different: "Anders erklären",
    example: "Gib mir ein Beispiel",
    read: "Lies es mir vor",
    addMaterial: "Material hinzufügen",
    materialPlaceholder: "Notizen einfügen oder Foto / Audio anhängen…",
    materialTitle: "Materialname",
    save: "Speichern",
    noMaterials: "Noch kein Material vorhanden.",
  },
  knowledge: {
    to_start: "Noch zu beginnen",
    learning: "Am Lernen",
    almost: "Fast bereit",
    ready: "Bereit",
    review: "Zu wiederholen",
  },
  wizard: {
    cta: "DIESES THEMA LERNEN",
    when: "Wann ist deine Prüfung?",
    howLong: "Wie lange kannst du lernen?",
    from: "Woraus möchtest du lernen?",
    sourceApp: "App-Theorie",
    sourceMine: "Meine Materialien",
    sourceBoth: "Beides",
    how: "Wie möchtest du dich vorbereiten?",
    styleAi: "Von der KI empfohlener Plan",
    styleFast: "Schnell",
    styleDeep: "Vertieft",
    styleReview: "Wiederholung",
    styleCustom: "Individuell",
    notes: "Sonst noch etwas, das die KI wissen sollte?",
    create: "Meinen Plan erstellen",
    creating: "Dein Plan wird erstellt…",
    custom: "Individuell",
  },
  plan: {
    title: "Dein Plan",
    daysLeft: "Noch {{count}} Tage",
    tomorrow: "PRÜFUNG MORGEN",
    today: "HEUTE",
    needed: "Geschätzte benötigte Zeit",
    start: "Starten",
    done: "Erledigt",
    regenerate: "Plan neu organisieren",
    missed: "Du hast die gestrige Sitzung nicht beendet. Möchtest du den Plan neu organisieren?",
    inPlanner: "Sitzungen zu deinem Planer hinzugefügt",
  },
  quick: {
    title: "Ich habe wenig Zeit",
    minutes: "{{n}} Minuten",
    go: "Was sollte ich jetzt tun?",
  },
  toolsFor: "Empfohlene Werkzeuge für dieses Fach",
};

const pt: typeof en = {
  title: "Programas",
  subtitle: "O teu ano letivo, transformado num plano de estudo",
  yourYear: "O teu ano letivo",
  completed: "do programa concluído",
  empty: "Ainda não há nenhum programa. Importa o teu programa para começar.",
  import: "Importa o teu programa",
  importDesc:
    "Carrega um PDF, uma foto do teu programa, cola o texto ou escreve-o. A IA transforma-o em unidades e tópicos — podes corrigir tudo antes de guardar.",
  classeviva:
    "A sincronização oficial com o ClasseViva ainda não está disponível: assim que existir uma API autorizada, esta secção será preenchida automaticamente, sem nada mudar para ti.",
  paste: "Cola ou escreve aqui o teu programa…",
  analyze: "Analisar com IA",
  analyzing: "A ler o teu programa…",
  review: "Verifica e corrige",
  confirm: "Confirmar importação",
  subject: "Disciplina",
  teacher: "Professor",
  schoolYear: "Ano letivo",
  unit: "Unidade",
  topics: "tópicos",
  addTopic: "Adicionar tópico",
  addUnit: "Adicionar unidade",
  remove: "Remover",
  saved: "Programa importado",
  annualProgram: "Programa anual",
  legend: "✓ concluído · ◐ em curso · ○ não iniciado · ⭐ exame",
  status: {
    not_started: "Não iniciado",
    in_progress: "Em curso",
    done: "Concluído",
  },
  topicPage: {
    progress: "Progresso",
    estimated: "Tempo estimado",
    difficulty: "Dificuldade",
    knowledge: "Preparação",
    study: "Estudar",
    theory: "Teoria",
    exercises: "Exercícios",
    tools: "Ferramentas",
    materials: "Os meus materiais",
    appTheory: "Teoria da app",
    myTheory: "A minha teoria",
    generate: "Gerar teoria",
    simplify: "Simplificar",
    deepen: "Aprofundar",
    different: "Explica de outra forma",
    example: "Dá-me um exemplo",
    read: "Lê-me isto",
    addMaterial: "Adicionar material",
    materialPlaceholder: "Cola apontamentos, ou anexa uma foto / áudio…",
    materialTitle: "Nome do material",
    save: "Guardar",
    noMaterials: "Ainda não há material.",
  },
  knowledge: {
    to_start: "Por começar",
    learning: "A aprender",
    almost: "Quase pronto",
    ready: "Pronto",
    review: "A rever",
  },
  wizard: {
    cta: "ESTUDA ESTE TÓPICO",
    when: "Quando é o teu exame?",
    howLong: "Quanto tempo podes estudar?",
    from: "A partir de que queres estudar?",
    sourceApp: "Teoria da app",
    sourceMine: "Os meus materiais",
    sourceBoth: "Ambos",
    how: "Como queres preparar-te?",
    styleAi: "Plano recomendado pela IA",
    styleFast: "Rápido",
    styleDeep: "Aprofundado",
    styleReview: "Revisão",
    styleCustom: "Personalizado",
    notes: "Mais alguma coisa que a IA deva saber?",
    create: "Criar o meu plano",
    creating: "A criar o teu plano…",
    custom: "Personalizado",
  },
  plan: {
    title: "O teu plano",
    daysLeft: "Faltam {{count}} dias",
    tomorrow: "EXAME AMANHÃ",
    today: "HOJE",
    needed: "Tempo estimado necessário",
    start: "Começar",
    done: "Concluído",
    regenerate: "Reorganizar plano",
    missed: "Não terminaste a sessão de ontem. Queres reorganizar o plano?",
    inPlanner: "Sessões adicionadas ao teu Planeador",
  },
  quick: {
    title: "Não tenho muito tempo",
    minutes: "{{n}} minutos",
    go: "O que devo fazer agora?",
  },
  toolsFor: "Ferramentas sugeridas para esta disciplina",
};

export const programs: LangMap = { en, it, es, fr, de, pt };
